import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Tooltip } from 'primereact/tooltip';

import '../css/FileUpload.css';
import { AttachmentService } from '../../components/services/AttachmentService';
import saveFile from '../../helpers/saveFile';
import { useApiCall } from '../../hooks/useApiCall';
import { formatDateStringFromRaw } from '../../utils/date-utils';
import { withDialogFooterSpacing } from '../shared/dialogFooterSpacing';

interface FileWithError {
  file: File;
  error?: string;
  preview?: string;
}

interface FileUploadProps {
  taskId: number;
  setHasAttachments?: (hasAttachments: boolean) => void;
  onFilesUploaded?: () => void;
}

interface FileWithId {
  id: number;
  file: Partial<File>;
  modifiedByUser: string;
  modificationTime: string;
  compressedFormFile?: string;
  commentId?: number | null;
}

interface UploadResult {
  successCount: number;
  totalCount: number;
  failedFiles: Array<{ fileName: string; error: string }>;
}

const FileUpload: React.FC<FileUploadProps> = ({ taskId, setHasAttachments, onFilesUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const taskSectionRef = useRef<HTMLDivElement>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const [taskFiles, setTaskFiles] = useState<FileWithId[]>([]);
  const [commentFiles, setCommentFiles] = useState<FileWithId[]>([]);
  const [loading, setLoading] = useState(false);
  const [maxFileSize, setMaxFileSize] = useState(5000000);
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult>({
    successCount: 0,
    totalCount: 0,
    failedFiles: []
  });
  const [availableHeight, setAvailableHeight] = useState(0);

  const MAX_FILE_COUNT = 50;
  const [fileFormats, setFileFormats] = useState(
    '.png, .jpg, .xls, .xlsx, .doc, .docx, .pdf, .pptx'
  );

  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const containerTop = rect.top;
        const windowHeight = window.innerHeight;
        const available = windowHeight - containerTop - 40;
        setAvailableHeight(available);
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const validateFiles = useCallback(
    (selectedFiles: File[], existingUploadedFiles: FileWithId[]): FileWithError[] => {
      const validatedFiles: FileWithError[] = [];
      const currentFileCount = existingUploadedFiles.length;
      
      selectedFiles.forEach((file) => {
        let error: string | undefined = undefined;
        
        if (currentFileCount + validatedFiles.length >= MAX_FILE_COUNT) {
          error = `Достигнут лимит в ${MAX_FILE_COUNT} файлов.`;
        } else if (file.size > maxFileSize) {
          error = `Файл ${file.name} превысил лимит размера 5MB.`;
        } else if (existingUploadedFiles.find(f => f.file.name === file.name)) {
          error = `Файл с именем ${file.name} уже добавлен.`;
        } else if (validatedFiles.find(f => f.file.name === file.name)) {
          error = `Файл с именем ${file.name} выбран более одного раза.`;
        }

        const fileWithError: FileWithError = { file, error };
        
        if (!error) {
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          if (fileExtension && ['png', 'jpg', 'jpeg'].includes(fileExtension)) {
            const reader = new FileReader();
            reader.onload = (e) => {
              fileWithError.preview = e.target?.result as string;
            };
            reader.readAsDataURL(file);
          }
        }
        
        validatedFiles.push(fileWithError);
      });

      return validatedFiles;
    },
    [maxFileSize]
  );

  const getFilesByTask = useCallback(async () => {
    setLoading(true);
    const result = await AttachmentService.getByTask(taskId);
    if (result.data) {
      const allFiles: FileWithId[] = result.data.map(
        (x: any): FileWithId => ({
          id: x.id,
          file: { name: x.name },
          modifiedByUser: x.modifiedByUser,
          modificationTime: x.modificationTime,
          compressedFormFile: x.compressedFormFile?.fileContents
            ? `data:${x.compressedFormFile.contentType};base64,${x.compressedFormFile.fileContents}`
            : undefined,
          commentId: x.commentId || null
        })
      );
      
      const taskAttachments = allFiles.filter(file => !file.commentId);
      const commentAttachments = allFiles.filter(file => file.commentId);
      
      setTaskFiles(taskAttachments);
      setCommentFiles(commentAttachments);
      setHasAttachments?.(taskAttachments.length > 0);
      if (onFilesUploaded) {
        onFilesUploaded();
      }
    }
    setLoading(false);
  }, [taskId, setHasAttachments, onFilesUploaded]);

  useEffect(() => {
    getFilesByTask();
  }, [taskId]);

  useEffect(() => {
    setHasAttachments?.(taskFiles.length > 0);
  }, [taskFiles.length, setHasAttachments]);

  const getFileConfiguration = useCallback(async () => {
    const result = await AttachmentService.getAttachmentConfiguration();
    if (result?.data) {
      setMaxFileSize(result.data.maxSizeMB * 1000000);
      setFileFormats(result.data.formats);
    }
  }, []);
  
  useApiCall(getFileConfiguration);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setLoading(true);
    
    const validatedFiles = validateFiles(selectedFiles, taskFiles);
    const validFiles = validatedFiles.filter(f => !f.error);
    const invalidFiles = validatedFiles.filter(f => f.error);
    
    const successfulUploads: Array<{ success: true; fileName: string }> = [];
    const failedUploads: Array<{ fileName: string; error: string }> = [];

    for (const fileWithError of validFiles) {
      try {
        const formData = new FormData();
        formData.append('FormFile', fileWithError.file);
        formData.append('TaskId', taskId.toString());
        formData.append('Name', fileWithError.file.name);
        await AttachmentService.add(formData);
        successfulUploads.push({ success: true, fileName: fileWithError.file.name });
      } catch (error) {
        failedUploads.push({ 
          fileName: fileWithError.file.name, 
          error: 'Ошибка при загрузке файла' 
        });
      }
    }

    failedUploads.push(
      ...invalidFiles.map(f => ({ 
        fileName: f.file.name, 
        error: f.error! 
      }))
    );

    setUploadResult({
      successCount: successfulUploads.length,
      totalCount: selectedFiles.length,
      failedFiles: failedUploads
    });

    if (failedUploads.length > 0) {
      setShowUploadResult(true);
    }

    await getFilesByTask();
    setLoading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onFileRemove = async (fileName: string) => {
    const fileToRemove = taskFiles.find(f => f.file.name === fileName);
    if (fileToRemove) {
      setLoading(true);
      await AttachmentService.delete(fileToRemove.id);
      await getFilesByTask();
      setLoading(false);
    }
  };

  const onFileDownload = async (file: FileWithId) => {
    if (file.file.name) {
      const result = await AttachmentService.get(file.id);
      await saveFile(result.data, file.file.name, '');
    }
  };

  const onFileDoubleClick = async (file: FileWithId) => {
    const imageFormats = ['png', 'jpg', 'jpeg', 'gif', 'svg'];
    const fileExtension = file.file.name?.split('.').pop()?.toLowerCase();

    if (imageFormats.includes(fileExtension || '')) {
      const result = await AttachmentService.get(file.id);
      let blob;

      if (fileExtension === 'svg') {
        blob = new Blob([result.data], { type: 'image/svg+xml' });
      } else {
        blob = new Blob([result.data], { type: `image/${fileExtension}` });
      }

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      onFileDownload(file);
    }
  };

  const uploadResultFooter = (
    <div>
      <Button 
        label="OK" 
        icon="pi pi-check" 
        onClick={() => setShowUploadResult(false)} 
        autoFocus 
      />
    </div>
  );

  const FileList = ({ files, showDeleteButton, emptyMessage }: { 
    files: FileWithId[]; 
    showDeleteButton: boolean;
    emptyMessage?: string;
  }) => (
    <div className="uploaded-files">
      {files.length === 0 ? (
        <div className="flex flex-column align-items-center justify-content-center p-4">
          <i className="pi pi-inbox text-4xl text-400 mb-2" />
          <p className="text-500 m-0 text-center">{emptyMessage || 'Нет файлов'}</p>
        </div>
      ) : (
        files.map((file, index) => (
          <div
            key={index}
            className="file-item flex align-items-center mb-2"
          >
            <div
              className="file-item--uploaded flex align-items-center"
              style={{ cursor: 'pointer' }}
              data-pr-tooltip="Дважды щелкните, чтобы открыть или скачать файл"
              data-pr-position="right"
              onDoubleClick={() => onFileDoubleClick(file)}
            >
              {file.compressedFormFile ? (
                <img
                  src={file.compressedFormFile}
                  alt={file.file.name}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <i className="pi pi-file" style={{ fontSize: '2em' }}></i>
              )}
              <span className="fileName ml-3">{file.file.name}</span>
            </div>
            <span className="modifiedByUser ml-auto text-500">
              Добавлено пользователем {file.modifiedByUser}{' '}
              {formatDateStringFromRaw(file.modificationTime, 'в')}
            </span>
            <Button
              type="button"
              icon="pi pi-download"
              className="p-button-rounded p-button-info ml-2"
              onClick={() => onFileDownload(file)}
            />
            {showDeleteButton && (
              <Button
                type="button"
                icon="pi pi-times"
                className="p-button-rounded p-button-danger ml-2"
                onClick={() => onFileRemove(file.file.name ?? '')}
              />
            )}
          </div>
        ))
      )}
    </div>
  );

  const sectionHeight = availableHeight > 0 ? Math.floor(availableHeight / 2) - 20 : 400;

  return (
    <div className="upload-files" ref={containerRef}>
      
      <Dialog 
        header="Результаты загрузки файлов" 
        visible={showUploadResult} 
        style={{ width: '50vw' }} 
        footer={uploadResultFooter}
        onHide={() => setShowUploadResult(false)}
        pt={withDialogFooterSpacing()}
      >
        <div className="upload-results">
          <p>
            Загружено {uploadResult.successCount} из {uploadResult.totalCount} файлов
          </p>
          
          {uploadResult.failedFiles.length > 0 && (
            <div className="failed-files mt-3">
              <h4>Файлы, которые не были загружены:</h4>
              {uploadResult.failedFiles.map((failedFile, index) => (
                <div key={index} className="failed-file-item mb-2">
                  <Message 
                    severity="error" 
                    text={`${failedFile.fileName}: ${failedFile.error}`} 
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>

      <Tooltip target=".file-item--uploaded" mouseTrack mouseTrackLeft={10} />

      <div 
        className="task-attachments-section" 
        ref={taskSectionRef}
        style={{ 
          height: commentFiles.length === 0 ? sectionHeight * 2 : sectionHeight, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="flex justify-content-between align-items-center mb-3 flex-shrink-0">
          <h3 style={{ margin: 0 }}>Вложения наряда</h3>
          <div className="flex align-items-center gap-3">
            <span className="text-sm">
              {taskFiles.length} / {MAX_FILE_COUNT} файлов
            </span>
            <ProgressBar
              value={(taskFiles.length / MAX_FILE_COUNT) * 100}
              showValue={false}
              style={{ width: '8rem', height: '8px' }}
              className={taskFiles.length >= MAX_FILE_COUNT ? 'red' : ''}
            />
            <Button
              label="Загрузить файлы"
              icon="pi pi-upload"
              outlined
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={taskFiles.length >= MAX_FILE_COUNT || loading}
            />
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <ProgressSpinner />
          </div>
        ) : (
          <ScrollPanel style={{ width: '100%', height: `${(commentFiles.length === 0 ? sectionHeight * 2 : sectionHeight) - 70}px` }}>
            <FileList 
              files={taskFiles} 
              showDeleteButton={true} 
              emptyMessage="Нет общих вложений"
            />
          </ScrollPanel>
        )}
      </div>

      {taskFiles.length > 0 && commentFiles.length > 0 && (
        <Divider type="solid" style={{ margin: '8px 0' }} />
      )}

      {commentFiles.length > 0 && (
        <div 
          className="comment-attachments-section" 
          ref={commentSectionRef}
          style={{ 
            height: sectionHeight, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            marginTop: taskFiles.length > 0 ? '8px' : 0
          }}
        >
          <h3 style={{ marginBottom: '12px', flexShrink: 0 }}>Вложения из примечаний</h3>
          
          <ScrollPanel style={{ width: '100%', height: `${sectionHeight - 60}px` }}>
            <FileList 
              files={commentFiles} 
              showDeleteButton={false} 
              emptyMessage="Нет вложений в примечаниях"
            />
          </ScrollPanel>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={fileFormats}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      
    </div>
  );
};

export default FileUpload;

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}